import * as React from 'react';

import { cn, formatarData, formatarDataHora } from '@/lib/utils';
import {
  dataHoraAtendimento,
  nascimentoComIdade,
  simNao,
  VAZIO,
} from '@/features/marcacao/formato';
import type { MarcacaoEsus } from '@/features/marcacao/tipos';

/**
 * Cor da classificação de prioridade, como o eSUS pinta: do vermelho
 * (prioridade 0) ao azul (prioridade 3).
 */
const COR_PRIORIDADE: Record<number, string> = {
  0: 'text-[#d32f2f]',
  1: 'text-[#ed8b00]',
  2: 'text-[#2e7d32]',
  3: 'text-[#2196f3]',
};

/**
 * Folha A4 no layout da impressão do eSUS Regulação. Cores fixas, de papel,
 * independentemente do tema da tela.
 */
export function FolhaMarcacao({
  marcacao,
  chaveConfirmacao,
  impressoPor,
}: {
  marcacao: MarcacaoEsus;
  /** Digitada por quem imprime: o índice do eSUS não a traz. */
  chaveConfirmacao: string | null;
  impressoPor: string | null;
}) {
  const { agendamento: ag, paciente: pac, condicoesClinicas: cc, solicitante: sol } = marcacao;

  return (
    <article
      aria-label="Comprovante de marcação"
      className="mx-auto w-full max-w-[920px] bg-white text-[13px] leading-snug text-[#333] shadow-sm print:max-w-none print:shadow-none"
    >
      <Secao titulo="Identificador da solicitação">
        <Campo rotulo="Identificador" largura="metade">
          <span className="text-[16px] font-normal text-[#1e73be]">{marcacao.identificador}</span>
        </Campo>
        {/* Não existe no índice do eSUS: vem digitada no modal, ou sai em branco. */}
        <Campo rotulo="Chave de Confirmação" largura="metade" valor={chaveConfirmacao} destaque />
      </Secao>

      <Secao titulo="Agendamento">
        <Campo rotulo="Nome do Estabelecimento Executante" valor={ag.estabelecimento} />
        <Campo rotulo="CNES" valor={ag.cnes} />
        <Campo rotulo="Telefone" valor={ag.telefone} />
        <Campo rotulo="Endereço" valor={ag.logradouro} />
        <Campo rotulo="Número" valor={ag.numero} />
        <Campo rotulo="Complemento" valor={ag.complemento} />
        <Campo rotulo="Bairro" valor={ag.bairro} />
        <Campo rotulo="Município" valor={ag.municipio} />
        <Campo rotulo="CEP" valor={ag.cep} />
        <Campo rotulo="Profissional Executante" valor={ag.profissional} largura="metade" />
        <Campo rotulo="Data e Horário do Atendimento" largura="metade">
          <span className="text-[16px] font-bold text-[#2e7d32]">
            {dataHoraAtendimento(ag) ?? VAZIO}
          </span>
        </Campo>
      </Secao>

      <Secao
        titulo={`Atendimento para ${marcacao.modulo?.toLowerCase() === 'exame' ? 'exame' : 'consulta'}`}
        semGrade
      >
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[#ddd] text-left text-[14px] font-normal uppercase text-[#333]">
              <th className="w-[57%] px-5 py-1.5 font-normal" />
              <th className="w-[20%] px-2 py-1.5 font-normal">Código Interno</th>
              <th className="px-2 py-1.5 font-normal">Código Unificado</th>
            </tr>
          </thead>
          <tbody>
            {marcacao.procedimentos.length === 0 ? (
              <tr>
                <td className="px-5 py-2 font-semibold" colSpan={3}>
                  {VAZIO}
                </td>
              </tr>
            ) : (
              marcacao.procedimentos.map((p, i) => (
                <tr key={i} className="text-[12px] font-semibold uppercase text-[#222]">
                  <td className="px-5 py-2">{p.nome ?? VAZIO}</td>
                  <td className="px-2 py-2">{p.codigoInterno ?? VAZIO}</td>
                  <td className="px-2 py-2">{p.codigoUnificado ?? VAZIO}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Secao>

      {/* O índice do eSUS não traz o preparo: a seção fica, vazia, como no eSUS. */}
      <Secao titulo="Preparo(s) para o procedimento(s)" semGrade>
        <div className="h-3" />
      </Secao>

      <Secao titulo="Dados do usuário" espacado>
        <Campo rotulo="CNS" valor={pac.cns} />
        <Campo rotulo="CPF (sem máscara na numeração)" valor={pac.cpf} />
        <Campo rotulo="Data de Nascimento" valor={nascimentoComIdade(pac.dataNascimento)} />
        <Campo rotulo="Nome" valor={pac.nome} />
        <Campo rotulo="Nome Social" valor={pac.nomeSocial} />
        <Campo rotulo="Tipo Sanguíneo" valor={pac.tipoSanguineo} />
        <Campo rotulo="Nome da Mãe" valor={pac.nomeMae} />
        <Campo rotulo="Sexo" valor={pac.sexo} />
        <Campo rotulo="Raça" valor={pac.racaCor} />
        <Campo rotulo="Logradouro" valor={pac.logradouro} />
        <Campo rotulo="Complemento" valor={pac.complemento} />
        <Campo rotulo="Etnia Indígena" valor={pac.etniaIndigena} />
        <Campo rotulo="Bairro" valor={pac.bairro} />
        <Campo rotulo="Município de Residência" valor={pac.municipio} />
        <Campo rotulo="Número" valor={pac.numero} />
        <Campo rotulo="CEP" valor={pac.cep} />
        <Campo
          rotulo="Telefone(s)"
          valor={pac.telefones.length > 0 ? pac.telefones.join(', ') : null}
          largura="doisTercos"
        />
      </Secao>

      <Secao titulo="Condições clínicas">
        <Campo rotulo="Justificativa" valor={cc.justificativa} largura="inteira" />
        <Campo rotulo="Descrição da Classificação" valor={cc.descricaoCid} largura="metade" />
        <div className="col-span-12 flex flex-col gap-2 sm:col-span-3">
          <Campo rotulo="CID-10" valor={cc.cid} largura="contido" />
          {/* CIAP não existe no índice do eSUS. */}
          <Campo rotulo="CIAP" valor={null} largura="contido" />
        </div>
        <Campo rotulo="Classificação de prioridade" largura="quarto">
          <span
            className={cn(
              'text-[16px] font-bold normal-case',
              COR_PRIORIDADE[cc.codigoClassificacaoRisco ?? 3] ?? COR_PRIORIDADE[3],
            )}
          >
            {cc.classificacaoRisco ?? VAZIO}
          </span>
        </Campo>
        <Campo rotulo="Oncológico" valor={simNao(cc.oncologico)} />
        <Campo rotulo="Diabetes" valor={simNao(cc.diabetes)} />
        <Campo rotulo="Alergia" valor={simNao(cc.alergia, cc.descricaoAlergia)} />
        <Campo rotulo="Hipertensão Arterial" valor={simNao(cc.hipertensao)} />
        <Campo
          rotulo="Usuário com Deficiência"
          valor={simNao(cc.deficiencia, cc.descricaoDeficiencia)}
        />
        <Campo rotulo="OCI (Oferta de Cuidados Integrados)" valor={simNao(cc.oci)} />
      </Secao>

      <Secao titulo="Solicitante">
        <Campo rotulo="Profissional" valor={sol.profissional} largura="metade" />
        <Campo rotulo="Conselho Profissional" valor={sol.conselho} largura="quarto" />
        <Campo rotulo="CPF (sem máscara na numeração)" valor={sol.cpf} largura="quarto" />
        <Campo rotulo="Nome do Estabelecimento" valor={sol.estabelecimento} largura="metade" />
        <Campo rotulo="CNES" valor={sol.cnes} largura="quarto" />
        <Campo rotulo="Telefone" valor={sol.telefone} largura="quarto" />
      </Secao>

      <Secao titulo="Dados da solicitação">
        <Campo
          rotulo="Data do pedido"
          valor={
            marcacao.dadosSolicitacao.dataPedido &&
            formatarData(marcacao.dadosSolicitacao.dataPedido)
          }
          largura="quarto"
        />
        <Campo
          rotulo="Data do Cadastro"
          valor={
            marcacao.dadosSolicitacao.dataCadastro &&
            formatarData(marcacao.dadosSolicitacao.dataCadastro)
          }
          largura="quarto"
        />
        <Campo
          rotulo="Tipo de Consulta"
          valor={marcacao.dadosSolicitacao.tipoConsulta}
          largura="quarto"
        />
        <Campo rotulo="Situação Atual" largura="quarto">
          <span className="text-[16px] font-bold normal-case text-[#222]">
            {marcacao.situacao ?? VAZIO}
          </span>
        </Campo>
        <Campo
          rotulo="Telessaúde"
          valor={simNao(marcacao.dadosSolicitacao.telessaude)}
          largura="quarto"
        />
      </Secao>

      <p className="px-5 pb-3 pt-2 text-right text-[10px] text-[#888]">
        Dados do eSUS Regulação · impresso pelo KAVI em {formatarDataHora(new Date())}
        {impressoPor ? ` por ${impressoPor}` : ''}
      </p>
    </article>
  );
}

// -----------------------------------------------------------------------------

function Secao({
  titulo,
  children,
  semGrade,
  espacado,
}: {
  titulo: string;
  children: React.ReactNode;
  semGrade?: boolean;
  espacado?: boolean;
}) {
  return (
    <section className="break-inside-avoid">
      <h2 className="border-y border-[#ddd] bg-[#eee] px-5 py-1.5 text-[14px] font-bold uppercase tracking-wide text-[#555]">
        {titulo}
      </h2>
      {semGrade ? (
        children
      ) : (
        <dl
          className={cn('grid grid-cols-12 gap-x-4 px-5 py-2', espacado ? 'gap-y-3' : 'gap-y-1.5')}
        >
          {children}
        </dl>
      )}
    </section>
  );
}

const LARGURAS = {
  terco: 'col-span-12 sm:col-span-4',
  doisTercos: 'col-span-12 sm:col-span-8',
  metade: 'col-span-12 sm:col-span-6',
  quarto: 'col-span-12 sm:col-span-3',
  inteira: 'col-span-12',
  contido: '',
} as const;

function Campo({
  rotulo,
  valor,
  largura = 'terco',
  destaque,
  children,
}: {
  rotulo: string;
  valor?: string | null;
  largura?: keyof typeof LARGURAS;
  /** Valor em corpo maior, como o eSUS imprime a chave de confirmação. */
  destaque?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className={cn('min-w-0 break-words', LARGURAS[largura])}>
      <dt className="text-[14px] font-normal tracking-wide text-[#666]">{rotulo}</dt>
      <dd
        className={cn(
          'font-semibold uppercase text-[#222]',
          destaque ? 'text-[16px] font-bold' : 'text-[12px]',
        )}
      >
        {children ?? (valor?.trim() ? valor : VAZIO)}
      </dd>
    </div>
  );
}
